/**
 * The medication list — prescribing, amending and the pharmacy behind it.
 *
 * Replaces the `MedGrid`, `MedicationInsert`, `MedicationEdit` and
 * `MedicationUpdate` keyon envelopes and the read half of `MedicationAdmin`:
 * `PopulateMedGrid`, `PopulateMedGridByOrderID`, `GetPatientMeds`,
 * `GetPatientsMedByID`, `GetPrePourMeds`, `GetOrderedMedEditInfo`,
 * `InsertMedication`, `UpdateMedication`, `DeleteMedication`,
 * `UpdateMedAdminAvailable`, `UpdateMedAdminDoseUOM`,
 * `InsertAdminMecationInfo` (*sic*), `GetAssignedPatPharmacy` and
 * `UpdatePrepourForActiveMedications`.
 *
 * Giving, holding and stopping a drug are **not** here — they are clinical acts
 * on a patient, they carry `medications:administer` rather than
 * `medications:write`, and they live in `medicationAdministration.js`.
 *
 * Seven legacy reads collapse onto one list: `?filter[status]=active` is the
 * current-meds view, `?filter[is_prepour]=true` the pre-pour view,
 * `?filter[is_prn]=true` the rescue-dose view, `?filter[is_unordered]=true` the
 * ones added straight to the chart, and `?from=&to=` the date-ranged one.
 *
 * `{medication}` is an order-medication id. Order numbers are never accepted:
 * `ordernumber` was a non-unique `varchar(253)` allocated by an unlocked
 * `MAX(...)+1`, so a medication attached to whichever order the query found.
 */

import api from './client';

// --- the chart's medications -----------------------------------------------

export function listPatientMedications(patientId, params = {}) {
  return api.list(`/patients/${patientId}/medications`, params);
}

/** Straight onto the chart, with no order — `is_unordered` carries the intent. */
export function createPatientMedication(patientId, payload) {
  return api.post(`/patients/${patientId}/medications`, payload);
}

/** One switch across every live medication on the chart. */
export function setPrepourForActiveMedications(patientId, isPrepour) {
  return api.patch(`/patients/${patientId}/medications/prepour-setting`, {
    is_prepour: isPrepour,
  });
}

// --- one medication --------------------------------------------------------

export function getMedication(medicationId, params = {}) {
  return api.get(`/medications/${medicationId}`, params);
}

export function updateMedication(medicationId, patch) {
  return api.patch(`/medications/${medicationId}`, patch);
}

/** Soft, and refused once the line is live or a dose has been recorded. */
export function deleteMedication(medicationId) {
  return api.delete(`/medications/${medicationId}`);
}

/**
 * A new version that keeps the old one. The legacy edit overwrote the row, so a
 * dose already given pointed at instructions that no longer existed.
 */
export function reviseMedication(medicationId, payload) {
  return api.post(`/medications/${medicationId}/revisions`, payload);
}

// --- medications on an order -----------------------------------------------

export function listOrderMedications(orderId, params = {}) {
  return api.get(`/orders/${orderId}/medications`, params);
}

export function addOrderMedication(orderId, payload) {
  return api.post(`/orders/${orderId}/medications`, payload);
}

/**
 * Prescribe: writes the order and its medication in one transaction. It needs
 * `medications:write` **and** `orders:write`, because it genuinely creates both
 * — a client granted one of the two cannot prescribe.
 */
export function createMedicationOrder(patientId, payload) {
  return api.post(`/patients/${patientId}/medication-orders`, payload);
}

// --- pharmacies, prescriptions, prescribers --------------------------------

export function listPatientPharmacies(patientId, params = {}) {
  return api.get(`/patients/${patientId}/pharmacies`, params);
}

/** Resolves the pharmacy in the directory first, so two charts share one row. */
export function addPatientPharmacy(patientId, payload) {
  return api.post(`/patients/${patientId}/pharmacies`, payload);
}

export function listPrescriptions(patientId, params = {}) {
  return api.list(`/patients/${patientId}/prescriptions`, params);
}

export function createPrescription(patientId, payload) {
  return api.post(`/patients/${patientId}/prescriptions`, payload);
}

export function addPrescriber(patientId, payload) {
  return api.post(`/patients/${patientId}/prescribers`, payload);
}

/**
 * Public federal pharmacy reference data — `{name, city, state, postal_code,
 * telephone}`. No chart in it, so it is the one read in this module with no
 * care-relationship check and no audit type.
 */
export function searchPharmacyRegistry(params) {
  return api.get('/pharmacy-registry-search', params);
}

export default {
  listPatientMedications, createPatientMedication, setPrepourForActiveMedications,
  getMedication, updateMedication, deleteMedication, reviseMedication,
  listOrderMedications, addOrderMedication, createMedicationOrder,
  listPatientPharmacies, addPatientPharmacy,
  listPrescriptions, createPrescription, addPrescriber,
  searchPharmacyRegistry,
};
